import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserService } from '../../../domain/user-service';
import { HttpService } from '../../http/http';
import {
  PublishingEvent,
  PublishingPlatform,
  PublishingStatus,
  PublishingDateRangeKey,
} from '../../../domain/publishing-status';

export interface PublicationsPageResult {
  total: number;
  items: PublishingEvent[];
}

export interface GetPublicationsPageParams {
  limit: number;
  offset: number;
  query?: string;
  statusFilter: PublishingStatus | 'All';
  dateRangeKey: PublishingDateRangeKey;
  timeZone?: string;
}

/** List row from GET /api/publications. */
interface DestinationPublicationListItemApi {
  publication_uid: string;
  state: number;
  thumbnail: string;
  title: string;
  destination_display_name: string;
  platform_name: string;
  platform_icon_asset_key?: string | null;
  error_message?: string | null;
  external_url?: { bucket_url?: string; external_url?: string } | null;
  media_id?: number | null;
  created_at: string;
}

interface PublicationsPageApiResponse {
  total: number;
  items: DestinationPublicationListItemApi[];
}

/** Response from POST /api/publications/{publicationUid}/re-edit. */
interface ReEditPublicationApiResponse {
  publication_uid: string;
}

/**
 * Repository for destination publications list (GET /api/publications).
 */
@Injectable({ providedIn: 'root' })
export class PublishingRepository {
  constructor(
    private httpService: HttpService,
    private userService: UserService,
  ) {}

  /** Fork project from publication and create a new draft (POST /api/publications/{uid}/re-edit). */
  reEditPublication(publicationUid: string): Promise<{ publicationUid: string }> {
    return this.httpService
      .post(`publications/${publicationUid}/re-edit`, {})
      .then((body: ReEditPublicationApiResponse) => ({
        publicationUid: body.publication_uid,
      }));
  }

  /** Export selected publications into MAM (POST /api/publications/export-to-mam). */
  exportToMam(publicationUids: string[]): Promise<void> {
    return this.httpService
      .post('publications/export-to-mam', { publication_uids: publicationUids })
      .then(() => undefined);
  }

  /** Hard-delete selected publications (POST /api/publications/delete). */
  deletePublications(publicationUids: string[]): Promise<void> {
    return this.httpService
      .post('publications/delete', { publication_uids: publicationUids })
      .then(() => undefined);
  }

  /** Cancel a publication (POST /api/publications/{uid}/cancel). */
  cancelPublication(publicationUid: string): Promise<void> {
    return this.httpService
      .post(`publications/${publicationUid}/cancel`, {})
      .then(() => undefined);
  }

  getPublicationsPage(params: GetPublicationsPageParams): Observable<PublicationsPageResult> {
    const query = this.buildListQueryString(params);
    return from(this.httpService.get(`publications?${query}`)).pipe(
      map((body: PublicationsPageApiResponse) => ({
        total: body?.total ?? 0,
        items: (body?.items ?? []).map((item) => this.mapApiItemToPublishingEvent(item)),
      })),
    );
  }

  private buildListQueryString(params: GetPublicationsPageParams): string {
    const parts: string[] = [
      `Limit=${params.limit}`,
      `Offset=${params.offset}`,
    ];

    const trimmedQuery = params.query?.trim();
    if (trimmedQuery) {
      parts.push(`Query=${encodeURIComponent(trimmedQuery)}`);
    }

    const statusParams = this.mapStatusToApi(params.statusFilter);
    if (statusParams.state != null) {
      parts.push(`State=${statusParams.state}`);
    }
    if (statusParams.minState != null) {
      parts.push(`MinState=${statusParams.minState}`);
    }
    if (statusParams.maxState != null) {
      parts.push(`MaxState=${statusParams.maxState}`);
    }

    if (params.dateRangeKey !== 'all') {
      parts.push(`DateRange=${encodeURIComponent(params.dateRangeKey)}`);
      const tz = params.timeZone?.trim() || this.userService.getDisplayTimezone();
      parts.push(`TimeZone=${encodeURIComponent(tz)}`);
    }

    return parts.join('&');
  }

  private mapStatusToApi(status: PublishingStatus | 'All'): {
    state?: number;
    minState?: number;
    maxState?: number;
  } {
    switch (status) {
      case PublishingStatus.Draft:
        return { state: -10 };
      case PublishingStatus.Scheduled:
        return { state: 0 };
      case PublishingStatus.Rendering:
        return { minState: 10, maxState: 49 };
      case PublishingStatus.Processing:
        return { minState: 50, maxState: 99 };
      case PublishingStatus.PostUploadProcessing:
        return { state: 100 };
      case PublishingStatus.Success:
        return { state: 110 };
      case PublishingStatus.SuccessWithWarnings:
        return { state: 120 };
      case PublishingStatus.Failed:
        return { state: 200 };
      case PublishingStatus.Cancelled:
        return { state: 250 };
      default:
        return {};
    }
  }

  private mapApiItemToPublishingEvent(
    item: DestinationPublicationListItemApi,
  ): PublishingEvent {
    const isDraft = item.state === -10;

    return {
      id: item.publication_uid,
      clipTitle: item.title || undefined,
      platform: this.mapPlatformName(item.platform_name),
      account: item.destination_display_name || undefined,
      status: this.mapPublicationState(item.state),
      createdAt: item.created_at,
      thumbnail: item.thumbnail || '/assets/publishing-status/share.svg',
      errorMessage: item.error_message ?? undefined,
      external_url: item.external_url?.external_url ?? undefined,
      media_id: item.media_id != null && item.media_id > 0 ? item.media_id : null,
      sourceEditUrl: isDraft
        ? `/clipping-studio/${item.publication_uid}/edit`
        : undefined,
    };
  }

  private mapPlatformName(name: string): PublishingPlatform | undefined {
    const normalized = (name ?? '').trim().toLowerCase();
    if (!normalized) return undefined;
    const entry = Object.entries(PublishingPlatform).find(
      ([, value]) => value.toLowerCase() === normalized,
    );
    return entry ? (entry[1] as PublishingPlatform) : undefined;
  }

  private mapPublicationState(state: number): PublishingStatus {
    if (state === -10) return PublishingStatus.Draft;
    if (state === 0) return PublishingStatus.Scheduled;
    if (state === 100) return PublishingStatus.PostUploadProcessing;
    if (state === 110) return PublishingStatus.Success;
    if (state === 120) return PublishingStatus.SuccessWithWarnings;
    if (state === 200) return PublishingStatus.Failed;
    if (state === 250) return PublishingStatus.Cancelled;
    if (state >= 10 && state < 50) return PublishingStatus.Rendering;
    if (state >= 50 && state < 100) return PublishingStatus.Processing;
    return PublishingStatus.Processing;
  }
}
